import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
  FileText, 
  UserCheck, 
  Wrench, 
  AlertTriangle, 
  Package, 
  CheckCircle, 
  Truck,
  FileCheck,
  Clock,
  RotateCcw
} from 'lucide-react';

const JobCardStatusWorkflow = ({ jobCard, onStatusChange, onReworkReasonChange, disabled = false, isAdmin = false }) => {
  const [showReworkBox, setShowReworkBox] = useState(false);
  const [reworkReason, setReworkReason] = useState('');
  const statuses = [
    { 
      value: 'created', 
      label: 'Created', 
      color: 'bg-gray-100 text-gray-800',
      icon: FileText,
      description: 'Job card has been created'
    },
    { 
      value: 'assigned', 
      label: 'Assigned', 
      color: 'bg-blue-100 text-blue-800',
      icon: UserCheck,
      description: 'Job assigned to technician'
    },
    { 
      value: 'in-progress', 
      label: 'In Progress', 
      color: 'bg-yellow-100 text-yellow-800',
      icon: Wrench,
      description: 'Work is being performed'
    },
    { 
      value: 'on-hold', 
      label: 'On Hold', 
      color: 'bg-red-100 text-red-800',
      icon: AlertTriangle,
      description: 'Work is temporarily stopped'
    },
    { 
      value: 'waiting-parts', 
      label: 'Waiting Parts', 
      color: 'bg-orange-100 text-orange-800',
      icon: Package,
      description: 'Waiting for parts to arrive'
    },
    { 
      value: 'quality-check', 
      label: 'Quality Check', 
      color: 'bg-purple-100 text-purple-800',
      icon: CheckCircle,
      description: 'Work completed, under review'
    },
    { 
      value: 'completed', 
      label: 'Completed', 
      color: 'bg-green-100 text-green-800',
      icon: CheckCircle,
      description: 'All work completed successfully'
    },
    { 
      value: 'delivered', 
      label: 'Delivered', 
      color: 'bg-emerald-100 text-emerald-800',
      icon: Truck,
      description: 'Vehicle delivered to customer'
    },
    { 
      value: 'invoiced', 
      label: 'Invoiced', 
      color: 'bg-indigo-100 text-indigo-800',
      icon: FileCheck,
      description: 'Invoice generated and sent'
    },
    {
      value: 'rework',
      label: 'Rework',
      color: 'bg-rose-100 text-rose-800',
      icon: RotateCcw,
      description: 'Vehicle returned after delivery/invoicing for rework'
    }
  ];

  const getCurrentStatus = () => {
    return statuses.find(status => status.value === jobCard.status) || statuses[0];
  };

  const getValidNextStatuses = (currentStatus) => {
    // Admins get full freedom to correct mistakes — any status is reachable from any status.
    if (isAdmin) {
      return statuses.map(s => s.value).filter(v => v !== currentStatus);
    }
    const statusFlow = {
      'created': ['assigned'],
      'assigned': ['created', 'in-progress', 'on-hold'],
      'in-progress': ['assigned', 'on-hold', 'waiting-parts', 'quality-check', 'completed'],
      'on-hold': ['assigned', 'in-progress'],
      'waiting-parts': ['in-progress', 'on-hold'],
      'quality-check': ['in-progress', 'completed'],
      'completed': ['quality-check', 'delivered', 'invoiced'],
      'delivered': ['completed', 'invoiced'],
      'invoiced': ['delivered', 'completed'],
      'rework': ['in-progress', 'quality-check']
    };
    
    return statusFlow[currentStatus] || [];
  };

  const canTransitionTo = (targetStatus) => {
    const validNext = getValidNextStatuses(jobCard.status);
    return validNext.includes(targetStatus);
  };

  const handleStatusChange = (newStatus) => {
    if (canTransitionTo(newStatus)) {
      onStatusChange(newStatus);
    }
  };

  const daysSinceCompleted = jobCard.completed_at
    ? Math.floor((Date.now() - new Date(jobCard.completed_at).getTime()) / 86400000)
    : null;

  const handleReopenForRework = () => {
    setShowReworkBox(true);
  };

  const handleConfirmReopenForRework = () => {
    if (onReworkReasonChange) onReworkReasonChange(reworkReason.trim());
    onStatusChange('rework');
    setShowReworkBox(false);
    setReworkReason('');
  };

  const handleCancelReopenForRework = () => {
    setShowReworkBox(false);
    setReworkReason('');
  };

  const currentStatus = getCurrentStatus();
  const CurrentIcon = currentStatus.icon;
  const validNextStatuses = getValidNextStatuses(jobCard.status);
  const canReopenForRework = ['completed', 'delivered', 'invoiced'].includes(jobCard.status);

  return (
    <div className="space-y-4">
      {/* Current Status Display */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
        <CurrentIcon className="h-6 w-6 text-gray-600" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge className={currentStatus.color}>
              {currentStatus.label}
            </Badge>
            {jobCard.is_on_hold && (
              <Badge className="bg-red-100 text-red-800 animate-pulse">
                <AlertTriangle className="h-3 w-3 mr-1" />
                ON HOLD
              </Badge>
            )}
            {jobCard.rework_count > 0 && (
              <Badge className="bg-rose-100 text-rose-800" data-testid="jobcard-rework-count-badge">
                <RotateCcw className="h-3 w-3 mr-1" />
                Reworked {jobCard.rework_count}x
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">{currentStatus.description}</p>
          {jobCard.last_activity_at && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
              <Clock className="h-3 w-3" />
              Last activity: {new Date(jobCard.last_activity_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Reopen for Rework — vehicle came back after Completed/Delivered/Invoiced */}
      {!disabled && canReopenForRework && !showReworkBox && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleReopenForRework}
          className="text-rose-600 border-rose-300 hover:bg-rose-50"
          data-testid="reopen-for-rework-btn"
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Reopen for Rework
        </Button>
      )}

      {!disabled && canReopenForRework && showReworkBox && (
        <div className="p-3 border border-rose-200 bg-rose-50 rounded-lg space-y-2" data-testid="rework-reason-box">
          <label className="text-sm font-medium text-rose-800">
            Why is the vehicle coming back for rework?
            {daysSinceCompleted !== null && daysSinceCompleted > 30 && (
              <span className="block text-xs font-normal text-amber-700 mt-0.5">
                Note: this job was completed {daysSinceCompleted} days ago (over 1 month) — please confirm the warranty/rework window still applies.
              </span>
            )}
          </label>
          <Textarea
            value={reworkReason}
            onChange={(e) => setReworkReason(e.target.value)}
            placeholder="e.g. Customer reported the same noise returned after 2 weeks"
            rows={3}
            data-testid="rework-reason-textarea"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmReopenForRework}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-reopen-rework-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Confirm Reopen
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleCancelReopenForRework}
              data-testid="cancel-reopen-rework-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Status Change Controls */}
      {!disabled && validNextStatuses.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Change Status To: {isAdmin && <span className="text-xs text-blue-600 font-normal">(Admin: full access to any status)</span>}
          </label>
          <Select onValueChange={handleStatusChange} disabled={disabled}>
            <SelectTrigger data-testid="jobcard-status-select">
              <SelectValue placeholder="Select new status" />
            </SelectTrigger>
            <SelectContent>
              {validNextStatuses.map(statusValue => {
                const status = statuses.find(s => s.value === statusValue);
                const StatusIcon = status.icon;
                return (
                  <SelectItem key={statusValue} value={statusValue}>
                    <div className="flex items-center gap-2">
                      <StatusIcon className="h-4 w-4" />
                      <span>{status.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Status Workflow Visual */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-700">Workflow Progress</h4>
        <div className="grid grid-cols-3 gap-2">
          {statuses.slice(0, 9).map((status, index) => {
            const StatusIcon = status.icon;
            const isCurrentStatus = status.value === jobCard.status;
            const isCompleted = statuses.findIndex(s => s.value === jobCard.status) > index;
            const isAvailable = canTransitionTo(status.value);
            
            return (
              <div
                key={status.value}
                className={`p-2 rounded text-center border transition-all ${
                  isCurrentStatus 
                    ? 'border-blue-500 bg-blue-50' 
                    : isCompleted
                    ? 'border-green-300 bg-green-50'
                    : isAvailable && !disabled
                    ? 'border-yellow-300 bg-yellow-50 cursor-pointer hover:bg-yellow-100'
                    : 'border-gray-200 bg-gray-50'
                }`}
                onClick={() => isAvailable && !disabled && handleStatusChange(status.value)}
              >
                <StatusIcon className={`h-4 w-4 mx-auto mb-1 ${
                  isCurrentStatus 
                    ? 'text-blue-600' 
                    : isCompleted
                    ? 'text-green-600'
                    : isAvailable
                    ? 'text-yellow-600'
                    : 'text-gray-400'
                }`} />
                <div className={`text-xs font-medium ${
                  isCurrentStatus 
                    ? 'text-blue-800' 
                    : isCompleted
                    ? 'text-green-800'
                    : isAvailable
                    ? 'text-yellow-800'
                    : 'text-gray-500'
                }`}>
                  {status.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Action Buttons */}
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          {jobCard.status === 'created' && canTransitionTo('assigned') && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleStatusChange('assigned')}
              className="text-blue-600 hover:text-blue-700"
            >
              <UserCheck className="h-4 w-4 mr-1" />
              Assign Technician
            </Button>
          )}
          
          {jobCard.status === 'assigned' && canTransitionTo('in-progress') && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleStatusChange('in-progress')}
              className="text-yellow-600 hover:text-yellow-700"
            >
              <Wrench className="h-4 w-4 mr-1" />
              Start Work
            </Button>
          )}
          
          {jobCard.status === 'in-progress' && canTransitionTo('completed') && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleStatusChange('completed')}
              className="text-green-600 hover:text-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Mark Complete
            </Button>
          )}
          
          {jobCard.status === 'completed' && canTransitionTo('delivered') && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleStatusChange('delivered')}
              className="text-emerald-600 hover:text-emerald-700"
            >
              <Truck className="h-4 w-4 mr-1" />
              Mark Delivered
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default JobCardStatusWorkflow;